import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useActiveLang, withLocale } from "@/i18n/LocaleLink";
import { MENUS, PRIMARY_NAV } from "@/constants/navigation";
import { iconKeyFor, TILES } from "@/constants/icons";
import site from "@/config/site";
import { ROUTES } from "@/constants/routes";
import { Icon } from "@/components/ui";
import useScrolled from "@/hooks/useScrolled";
import useMediaQuery from "@/hooks/useMediaQuery";
import useKeyDown from "@/hooks/useKeyDown";
import "./Navbar.css";

const MENU_TABS = ["s2p", "solutions", "platform"];
const S2P_GROUP_KEYS = ["vendorManagement", "other"];
const LANGUAGES = [
  { code: "EN", lang: "en", label: "English" },
  { code: "DE", lang: "de", label: "Deutsch" },
];

export function Navbar({ theme = "light" }) {
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const [langOpen, setLangOpen] = useState(false);

  const scrolled = useScrolled(8);
  const compact = useMediaQuery("(max-width: 999px)");
  const location = useLocation();
  const navigate = useNavigate();
  const activeLang = useActiveLang();
  const dark = theme === "dark";

  const closeAll = useCallback(() => {
    setOpenMenu(null);
    setMobileOpen(false);
    setLangOpen(false);
  }, []);

  useKeyDown("Escape", closeAll);
  useEffect(() => closeAll(), [location.pathname, closeAll]);

  const switchLanguage = (nextLang) => {
    document.cookie = `lang=${nextLang}; path=/; max-age=31536000`;
    const bare = activeLang === "de" ? location.pathname.replace(/^\/de/, "") || "/" : location.pathname;
    navigate(`${withLocale(bare, nextLang)}${location.search}${location.hash}`);
    setLangOpen(false);
  };

  const menu = !compact && openMenu ? MENUS[openMenu] : null;

  const groups = useMemo(() => {
    if (!menu) return [];
    if (menu.groups) return menu.groups;
    return [
      {
        heading: "",
        sub: "",
        items: menu.items.map((item, i) => ({ ...item, tile: TILES[i % TILES.length] })),
      },
    ];
  }, [menu]);

  return (
    <header
      className={`nav nav--${theme}${scrolled ? " is-scrolled" : ""}`}
      role="banner"
      onMouseLeave={() => setOpenMenu(null)}
    >
      <div className="nav__bar">
        <div className="nav__inner">
          <Link to={ROUTES.home} className="nav__logo" aria-label={`${site.name} home`}>
            <img
              src={dark ? site.logo.cream : site.logo.navy}
              alt={site.name}
              width="140"
              height="28"
            />
          </Link>

          <nav className="nav__tabs" aria-label="Primary">
            {PRIMARY_NAV.map((tab) => (
              <NavLink
                key={tab.id}
                to={tab.to}
                end={tab.to === "/"}
                className={({ isActive }) =>
                  `nav__tab${isActive ? " is-active" : ""}${openMenu === tab.menu ? " is-hot" : ""}`
                }
                aria-haspopup={tab.menu ? "true" : undefined}
                aria-expanded={tab.menu ? openMenu === tab.menu : undefined}
                onMouseEnter={() => setOpenMenu(tab.menu ?? null)}
                onFocus={() => setOpenMenu(tab.menu ?? null)}
              >
                {t(`nav.primary.${tab.id}`)}
                {tab.menu ? (
                  <span className="nav__chev" aria-hidden="true">
                    ▾
                  </span>
                ) : null}
              </NavLink>
            ))}
          </nav>

          <div className="nav__actions">
            <div
              className="nav__lang"
              onMouseEnter={() => {
                setLangOpen(true);
                setOpenMenu(null);
              }}
              onMouseLeave={() => setLangOpen(false)}
            >
              <button
                type="button"
                className={`nav__langBtn${langOpen ? " is-open" : ""}`}
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                aria-label={t("common.languageLabel")}
                onClick={() => setLangOpen((v) => !v)}
              >
                <Icon name="globe" size={14} />
                {LANGUAGES.find((l) => l.lang === activeLang)?.label}
                <span className="nav__chev" aria-hidden="true">
                  ▾
                </span>
              </button>
              <div
                className={`nav__langMenu${langOpen ? " is-open" : ""}`}
                role="listbox"
                aria-label={t("common.languageLabel")}
              >
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    role="option"
                    aria-selected={activeLang === l.lang}
                    className={`nav__langOption${activeLang === l.lang ? " is-selected" : ""}`}
                    onClick={() => switchLanguage(l.lang)}
                  >
                    <span>{l.label}</span>
                    <span className="nav__langCode">{l.code}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className={`nav__burger${mobileOpen ? " is-open" : ""}`}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => {
                setMobileOpen((v) => !v);
                setOpenMenu(null);
              }}
            >
              <span />
              <span />
            </button>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------ desktop mega menu */}
      <div className={`nav__panel${menu ? " is-open" : ""}`} role="region" aria-label="Menu">
        {menu ? (
          <div className="nav__panelInner">
            <div className="nav__panelHead">
              <span className="nav__panelIcon">
                <Icon name={menu.icon} size={26} />
              </span>
              <div className="nav__panelCopy">
                <h2>{t(`nav.menus.${openMenu}.title`)}</h2>
                <p>{t(`nav.menus.${openMenu}.blurb`)}</p>
                <Link to={menu.href} className="nav__panelLink">
                  {t("nav.viewAll")}
                </Link>
              </div>
            </div>
            <div className="nav__panelGrid">
              {groups.map((group, gi) => {
                const groupKey = openMenu === "s2p" ? S2P_GROUP_KEYS[gi] : null;
                return (
                  <div key={group.heading || gi} className={gi ? "nav__group nav__group--split" : "nav__group"}>
                    {groupKey ? (
                      <div className="nav__groupHead">
                        <div className="nav__groupTitle">
                          {t(`nav.menus.s2p.groups.${groupKey}.heading`)}
                        </div>
                        <div className="nav__groupSub">{t(`nav.menus.s2p.groups.${groupKey}.sub`)}</div>
                      </div>
                    ) : null}
                    <div className={menu.groups ? "nav__items" : "nav__items nav__items--wide"}>
                      {group.items.map((item) => (
                        <Link key={item.fragment} to={item.href} className="nav__item">
                          <span className="nav__itemTile" style={{ background: item.tile }}>
                            <Icon name={iconKeyFor(item.label)} size={16} />
                          </span>
                          <span className="nav__itemLabel">{t(`nav.items.${item.fragment}.label`)}</span>
                          <span className="nav__itemArrow" aria-hidden="true">
                            ▸
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {/* ------------------------------------------------------ mobile menu */}
      <div className={`nav__mobile${mobileOpen && compact ? " is-open" : ""}`}>
        <div className="nav__mobileInner">
          <Link to={ROUTES.home} className="nav__mobileTop">
            {t("nav.primary.home")}
          </Link>
          {MENU_TABS.map((key) => {
            const group = MENUS[key];
            const isOpen = mobileGroup === key;
            return (
              <div key={key} className="nav__mobileGroup">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setMobileGroup(isOpen ? null : key)}
                >
                  {t(`nav.menus.${key}.kicker`)}
                  <span className={`nav__chev${isOpen ? " is-flipped" : ""}`} aria-hidden="true">
                    ▾
                  </span>
                </button>
                <div className={`nav__mobileBody${isOpen ? " is-open" : ""}`}>
                  <Link to={group.href} className="nav__mobileOverview">
                    {t("nav.mobileOverview", { title: t(`nav.menus.${key}.title`) })}
                  </Link>
                  {group.items.map((item) => (
                    <Link key={item.fragment} to={item.href} className="nav__mobileItem">
                      {t(`nav.items.${item.fragment}.label`)}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
          <Link to={ROUTES.about} className="nav__mobileTop">
            {t("nav.primary.about")}
          </Link>
          <Link to={ROUTES.contact} className="nav__mobileTop nav__mobileTop--last">
            {t("nav.primary.contact")}
          </Link>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
